/**
 * a2-25 — the stay form's Country, filled in from its Place.
 *
 * Doug chose: fields Type (the second home / Travel), Place, Country, with Country
 * filled from the place when it is known. "Known" is two lists and nothing
 * else: the places Doug has already given a country to (his own `stays`
 * rows — Wroclaw → PL, Vilnius → LT), and a short table of common places.
 * Anything else leaves Country empty. `placeEmoji.ts` says why: a wrong flag
 * is worse than none.
 *
 * The match is the whole place, not a word in it. `emojiFor` reads a label
 * for a flag with word-boundary regexes because a wrong guess there costs a
 * glyph; here a guess is written to the row, and "Rome trip with Paris
 * friends" is not a place this should answer for.
 *
 * Dependency-free (one `.ts` import, itself dependency-free), so the root
 * suite tests it: `test/year-ux.test.ts`.
 */
import { COUNTRIES } from './placeEmoji.ts'

/** The codes the Country select offers. A code outside it would be saved
 *  without the select being able to show it, so nothing else is answered. */
const OFFERED = new Set(COUNTRIES.map(([code]) => code))

/** Lower case, accents off, one space: "Wrocław " and "wroclaw" are one
 *  place. The Polish ł has no combining form, so it is mapped by hand. */
export function placeKey(place: string): string {
  return place.normalize('NFD').replace(/\p{M}/gu, '').replace(/ł/g, 'l').replace(/Ł/g, 'L')
    .toLowerCase().replace(/\s+/g, ' ').trim()
}

// A `Map`, not an object literal: `COMMON['constructor']` would answer out of
// `Object.prototype` (CLAUDE.md's tab-alias bug), and a place called
// "Constructor" would get a function for a country.
const COMMON = new Map<string, string>()
const add = (code: string, names: string[]) => { for (const n of names) COMMON.set(placeKey(n), code) }
// The countries' own names, then the cities a trip from Berlin is likeliest
// to be labelled with — the ones `emojiFor` already knows, and a few more.
for (const [code, name] of COUNTRIES) add(code, [name])
add('IE', ['Dublin', 'Cork', 'Galway', 'Limerick', 'Kerry', 'Knock', 'Shannon', 'Mayo'])
add('DE', ['Munich', 'München', 'Hamburg', 'Cologne', 'Köln', 'Leipzig', 'Dresden', 'Frankfurt', 'Deutschland'])
add('GB', ['London', 'Edinburgh', 'Manchester', 'Glasgow', 'Liverpool', 'Bristol', 'England', 'Scotland', 'Wales', 'UK', 'Britain'])
add('FR', ['Paris', 'Lyon', 'Marseille', 'Nice', 'Bordeaux', 'Frankreich'])
add('IT', ['Rome', 'Roma', 'Milan', 'Milano', 'Venice', 'Venezia', 'Naples', 'Florence', 'Firenze', 'Turin', 'Bologna', 'Sicily', 'Italien'])
add('ES', ['Madrid', 'Barcelona', 'Seville', 'Valencia', 'Malaga', 'Mallorca', 'Ibiza', 'Spanien'])
add('PT', ['Lisbon', 'Lisboa', 'Lissabon', 'Porto', 'Madeira'])
add('NL', ['Amsterdam', 'Rotterdam', 'The Hague', 'Utrecht', 'Holland'])
add('BE', ['Brussels', 'Brüssel', 'Antwerp', 'Ghent', 'Bruges'])
add('CH', ['Zurich', 'Zürich', 'Geneva', 'Basel', 'Bern', 'Lausanne'])
add('AT', ['Vienna', 'Wien', 'Salzburg', 'Innsbruck', 'Graz', 'Österreich'])
add('PL', ['Warsaw', 'Kraków', 'Krakow', 'Wrocław', 'Gdańsk', 'Poznań', 'Szczecin', 'Polen'])
add('CZ', ['Prague', 'Prag', 'Brno', 'Czech Republic'])
add('HU', ['Budapest'])
add('HR', ['Zagreb', 'Split', 'Dubrovnik'])
add('GR', ['Athens', 'Crete', 'Thessaloniki'])
add('DK', ['Copenhagen', 'Kopenhagen', 'Aarhus'])
add('SE', ['Stockholm', 'Gothenburg', 'Malmö'])
add('NO', ['Oslo', 'Bergen'])
add('LT', ['Vilnius', 'Kaunas', 'Litauen'])
add('TR', ['Istanbul'])
add('MA', ['Marrakech', 'Marrakesh'])
add('US', ['New York', 'USA', 'America'])
add('MX', ['Mexico City', 'Ciudad de México'])
add('JP', ['Tokyo', 'Kyoto', 'Osaka'])

/**
 * Place → country from Doug's own stays: every labelled row that carries a
 * country. A place he has given two different countries is left out — the
 * rows disagree, so neither is "known".
 */
export function learnedCountries(stays: readonly { label: string | null; country: string | null }[]): Map<string, string> {
  const seen = new Map<string, string | null>()
  for (const s of stays) {
    if (!s.label?.trim() || !s.country || !OFFERED.has(s.country)) continue
    const k = placeKey(s.label)
    const had = seen.get(k)
    seen.set(k, had === undefined || had === s.country ? s.country : null)
  }
  const out = new Map<string, string>()
  for (const [k, c] of seen) if (c) out.set(k, c)
  return out
}

/** The country for a place, or '' when it is not known. Doug's own rows
 *  first — he labelled it, so his answer wins over the table. */
export function countryForPlace(place: string, learned: ReadonlyMap<string, string>): string {
  const k = placeKey(place)
  if (!k) return ''
  return learned.get(k) ?? COMMON.get(k) ?? ''
}

/**
 * The stay form's state. `countryAuto` is its own field and not "country is
 * empty": an empty Country Doug chose ("No flag") must stay empty when he
 * edits the place, and a country the form filled in must follow the place
 * when he corrects a typo in it. Two meanings, two fields.
 */
export interface StayDraft {
  kind: 'home2' | 'travel'
  start: string
  end: string
  place: string
  country: string
  /** True until Doug picks a country himself; while true, Place fills it. */
  countryAuto: boolean
  provisional: boolean
}

export function stayDraft(today: string, initial?: {
  kind: string; starts_on: string; ends_on: string; label: string | null; country: string | null; provisional: boolean
}, /** What a new stay opens on: `home2` for a person with a second home, whose
     *  first Type option it is, otherwise the only one there is. */
  newKind: 'home2' | 'travel' = 'travel'): StayDraft {
  return {
    kind: initial ? (initial.kind === 'travel' ? 'travel' : 'home2') : newKind,
    start: initial?.starts_on ?? today,
    end: initial?.ends_on ?? today,
    place: initial?.label ?? '',
    country: initial?.country ?? '',
    // An edit of a row that already has a country keeps it, whatever the
    // place is changed to; one without follows the place like a new one.
    countryAuto: !initial?.country,
    provisional: initial?.provisional ?? false,
  }
}

export function withPlace(d: StayDraft, place: string, learned: ReadonlyMap<string, string>): StayDraft {
  return { ...d, place, country: d.countryAuto ? countryForPlace(place, learned) : d.country }
}

export function withCountry(d: StayDraft, country: string): StayDraft {
  return { ...d, country, countryAuto: false }
}

/** What the row gets. The second home carries its own flag (the profile's
 *  country), so no country on the row. */
export function stayFromDraft(d: StayDraft): {
  kind: 'home2' | 'travel'; starts_on: string; ends_on: string; label: string | null; provisional: boolean; country: string | null
} {
  return {
    kind: d.kind,
    starts_on: d.start <= d.end ? d.start : d.end,
    ends_on: d.start <= d.end ? d.end : d.start,
    label: d.place.trim() || null,
    provisional: d.provisional,
    country: d.kind === 'travel' && d.country ? d.country : null,
  }
}
